import { Formik, Form } from 'formik';
import React from 'react';
import usersValidationSchema from './validationSchema';
import Input from 'component/atom/forms/input/input/Input';
import Select from 'component/atom/forms/select/select/Select';
import styles from './UsersForm.module.scss';
import {
  groupEndpoints,
  scriptEndpoints,
  userEndpoints,
} from 'hooks/endpoints';
import { useUserContext } from 'app-context/user/UserContext';
import Loader from 'component/atom/loader/Loader';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import { useFetch } from 'hooks/useFetch';
import MultiSelectList from 'component/atom/forms/select/multi-select-list/MultiSelectList';

const UserForm = ({
  id,
  data,
  isFetching,
  formRef,
  AddUser,
  EditUser,
  onClose,
  setRefetch,
  refetch,
  setSelectedUser,
}) => {
  const { updateProfileImage } = useUserContext();

  const { data: groups } = useFetch(
    ['groups'],
    groupEndpoints?.getUserGroups(),
    { enabled: true },
    { pageSize: 1000, pageNumber: 1 },
  );

  const { data: bots } = useFetch(
    ['bots-app'],
    scriptEndpoints.getCompanyBots,
    {
      enabled: true,
    },
    {
      pageSize: 1000,
      pageNumber: 1,
    },
  );

  const fetchedData = data?.data?.user?.[0];

  const initialvalues = {
    firstName: fetchedData?.firstName || '',
    lastName: fetchedData?.lastName || '',
    displayEmail: fetchedData?.displayEmail || '',
    username: fetchedData?.username || '',
    department: fetchedData?.department || '',
    mobile: fetchedData?.mobile || '',
    phone: fetchedData?.phone || '',
    roleId: fetchedData?.roleId || null,
    userGroupId: fetchedData?.userGroupId || '',
    botIds: fetchedData?.botIds || [],
    ...(!id && { password: fetchedData?.password || '' }),
  };

  const rolesOptions = [
    { value: 0, label: 'Owner' },
    { value: 1, label: 'Admin' },
    { value: 2, label: 'User' },
  ];

  const groupOptions =
    groups?.data?.items?.map(({ id, groupName }) => ({
      value: id,
      label: groupName,
    })) || [];

  const botOptions =
    bots?.data?.items?.map(({ botId, botName }) => ({
      value: botId,
      label: botName,
    })) || [];

  const handleSubmitForm = (values) => {
    const userUpdates = { ...values };

    if (id) {
      EditUser(
        {
          url: userEndpoints?.editUser(id),
          data: values,
          method: 'Put',
        },
        {
          onSuccess: (data) => {
            if (
              data?.response?.userMessage ===
              'Email or username already exists.'
            ) {
              return;
            }

            updateProfileImage(null, id, userUpdates);
            setSelectedUser(null);
            setRefetch(!refetch);
            onClose();
          },
          onError: () => {},
        },
      );
    } else {
      AddUser(
        {
          url: userEndpoints?.createUser(),
          data: values,
        },
        {
          onSuccess: (data) => {
            if (
              data?.data?.userMessage === 'Email or username already exists.'
            ) {
              return;
            }

            updateProfileImage(null, null, userUpdates);
            setRefetch(!refetch);
            onClose();
          },
          onError: () => {},
        },
      );
    }
  };

  if (isFetching) {
    return <Loader isComponent />;
  }

  return (
    <Formik
      initialValues={initialvalues}
      validationSchema={usersValidationSchema(id)}
      enableReinitialize={true}
      onSubmit={(values) => {
        handleSubmitForm(values);
      }}
      innerRef={formRef}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleBlur,
        handleChange,
      }) => {
        const handleRoleChange = (e) => {
          const newRoleId = Number(e?.target?.value);

          handleChange(e);

          // when switching role manually, clear selected group
          if (values?.userGroupId) {
            setFieldValue('userGroupId', '');
            setFieldValue('botIds', []);
          }

          setFieldValue('roleId', newRoleId);
        };

        const handleGroupChange = (e) => {
          const selectedGroupId = e?.target?.value;

          handleChange(e);

          if (!selectedGroupId) return;

          const selectedGroup = groups?.data?.items?.find(
            (g) => g?.id === selectedGroupId,
          );

          const isAdminGroup = selectedGroup?.role === 'Admin';
          const isOwnerGroup = selectedGroup?.role === 'Owner';

          // group role Admin => role Admin, else User
          setFieldValue('roleId', isAdminGroup ? 1 : isOwnerGroup ? 0 : 2);

          // botIds reflect selected group's botIds
          setFieldValue('botIds', selectedGroup?.botIds || []);
        };

        const selectedGroup = values?.userGroupId
          ? groups?.data?.items?.find((g) => g?.id === values?.userGroupId)
          : null;

        const selectedGroupBotIds =
          selectedGroup?.botIds?.map((b) => `${b}`.toLowerCase()) || [];

        const displayedBotOptions = selectedGroup
          ? botOptions?.filter((b) =>
              selectedGroupBotIds.includes(`${b?.value}`.toLowerCase()),
            )
          : botOptions;

        return (
          <Form className={styles.formCont}>
            <div className={styles.cont}>
              <div className={styles.subCont}>
                <Input
                  label='First Name'
                  placeholder='Enter First Name'
                  name='firstName'
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.firstName}
                  touched={touched.firstName}
                />
                <Input
                  label='Last Name'
                  placeholder='Enter Last Name'
                  name='lastName'
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.lastName}
                  touched={touched.lastName}
                />
                <Input
                  label='Email Address'
                  placeholder='Enter Email'
                  name='displayEmail'
                  value={values.displayEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.displayEmail}
                  touched={touched.displayEmail}
                  disabled={id}
                />
                <Input
                  label='Department'
                  placeholder='Enter Department'
                  name='department'
                  value={values.department}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.department}
                  touched={touched.department}
                />
                <Input
                  label='Username'
                  placeholder='Enter Username'
                  name='username'
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.username}
                  touched={touched.username}
                  disabled={id}
                />
                <Input
                  label='Mobile'
                  placeholder='Enter mobile'
                  name='mobile'
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.mobile}
                  touched={touched.mobile}
                />
                <Input
                  label='Phone'
                  placeholder='Enter Phone'
                  name='phone'
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.phone}
                  touched={touched.phone}
                />

                {!id && (
                  <Input
                    label='Password'
                    placeholder='Enter Password'
                    name='password'
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.password}
                    touched={touched.password}
                    inputType='password'
                  />
                )}

                <Select
                  label='Role'
                  placeholder='Select Role'
                  name='roleId'
                  value={values.roleId}
                  onChange={handleRoleChange}
                  onBlur={handleBlur}
                  error={errors.roleId}
                  touched={touched.roleId}
                  options={rolesOptions}
                />

                <Select
                  label='Group (Optional)'
                  placeholder='Select Group'
                  name='userGroupId'
                  value={values.userGroupId}
                  onChange={handleGroupChange}
                  onBlur={handleBlur}
                  error={errors.userGroupId}
                  touched={touched.userGroupId}
                  options={groupOptions}
                />

                {(values?.roleId === 2 || values?.userGroupId) && (
                  <MultiSelectList
                    label='Chatbot'
                    name='botIds'
                    value={values.botIds}
                    onChange={handleChange}
                    options={displayedBotOptions}
                    placeholder='Select Bots'
                    error={errors.botIds}
                    touched={touched.botIds}
                    showTags={true}
                    disabled={!!values?.userGroupId}
                    cursorNotAllowed={true}
                    showTagClose={false}
                  />
                )}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default UserForm;
